/**
 * Form controls for the live Azure data in the Create-VM form:
 *  - LiveSelectField  — native dropdown with loading / empty / fallback states
 *  - SearchableSelect — searchable combobox (images, sizes) with a 300 ms debounced search
 *  - Combobox         — the shared list-box input (also used for the resource group, free text allowed)
 *  - CheckField / DisabledCheck — checkboxes ("not supported by the portal yet" is shown disabled, never as a fake switch)
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react'
import clsx from 'clsx'
import formStyles from '@/pages/NewDeploymentPage.module.css'
import styles from './AppriseCreateVm.module.css'
import { SEARCH_DEBOUNCE_MS, fallbackNote, type CatalogResult } from './appriseCatalog'

type CatalogState = Pick<CatalogResult<unknown>, 'isLoading' | 'source' | 'error'> & { isFetching?: boolean }

/** "Loading from Azure…" or the visible "Could not load from Azure: <error> — using built-in defaults". */
export function CatalogNote({ catalog }: { catalog?: CatalogState }) {
  if (!catalog) return null
  if (catalog.isLoading) return <span className={styles.muted} role="status">Loading from Azure…</span>
  if (catalog.source === 'fallback') return <span className={styles.catalogNote} role="status">{fallbackNote(catalog.error)}</span>
  return null
}

export interface ShellProps {
  label: string
  required?: boolean
  hint?: ReactNode
  error?: string
  warning?: string
  full?: boolean
  catalog?: CatalogState
  children: ReactNode
}

export function FieldFrame({ label, required, hint, error, warning, full, catalog, children }: ShellProps) {
  return (
    <div className={formStyles.formGroup} style={full ? { gridColumn: '1/-1' } : undefined}>
      <label className={formStyles.label}>
        {label} {required && <span className={formStyles.required}>*</span>}
      </label>
      {children}
      {error ? <span className={formStyles.errorMsg}>{error}</span>
        : warning ? <span className={formStyles.errorMsg} style={{ color: 'var(--aptean-warning)' }}>{warning}</span>
        : hint ? <div className={formStyles.fieldHint}>{hint}</div> : null}
      <CatalogNote catalog={catalog} />
    </div>
  )
}

export interface LiveOption {
  value: string
  label: string
  disabled?: boolean
}

interface LiveSelectFieldProps extends Omit<ShellProps, 'children'> {
  value: string
  options: LiveOption[]
  onChange: (v: string) => void
  emptyText?: string
  disabled?: boolean
  /** Label for the current value when the list does not contain it. */
  valueLabel?: string
  /** Disabled first option shown while nothing is chosen ("Choose one…"). */
  placeholder?: string
}

/** Native dropdown over a live list. The current value always stays visible, even if the list lacks it. */
export function LiveSelectField({ value, options, onChange, emptyText, disabled, valueLabel, placeholder, ...shell }: LiveSelectFieldProps) {
  const loading = !!shell.catalog?.isLoading
  const list = value && !options.some((o) => o.value === value) ? [{ value, label: valueLabel ?? value }, ...options] : options
  const empty = !loading && options.length === 0 && !value
  return (
    <FieldFrame {...shell} hint={empty && emptyText ? emptyText : shell.hint}>
      <select
        aria-label={shell.label}
        className={clsx(formStyles.select, shell.error && formStyles.inputError)}
        value={value}
        disabled={disabled || loading}
        onChange={(e) => onChange(e.target.value)}
      >
        {loading && list.length === 0 && <option value="">Loading from Azure…</option>}
        {empty && <option value="">{emptyText ?? 'Nothing available'}</option>}
        {!empty && !loading && placeholder && !value && <option value="" disabled>{placeholder}</option>}
        {list.map((o) => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
      </select>
    </FieldFrame>
  )
}

export interface ComboOption {
  id: string
  label: string
  description?: string
  disabled?: boolean
  selected?: boolean
}

interface ComboboxProps {
  ariaLabel: string
  inputValue: string
  onInputChange: (v: string) => void
  onPick: (id: string) => void
  options: ComboOption[]
  placeholder?: string
  loading?: boolean
  emptyText?: string
  invalid?: boolean
  disabled?: boolean
  mono?: boolean
}

/** Input + listbox. The input text is whatever the caller wants it to be (a search query or the value itself). */
export function Combobox({ ariaLabel, inputValue, onInputChange, onPick, options, placeholder, loading, emptyText = 'No matches', invalid, disabled, mono }: ComboboxProps) {
  const listId = useId()
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)

  const pick = (o: ComboOption) => {
    if (o.disabled) return
    onPick(o.id)
    setOpen(false)
    setActive(-1)
  }

  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      setOpen(true)
      const dir = e.key === 'ArrowDown' ? 1 : -1
      setActive((a) => (options.length ? (a + dir + options.length) % options.length : -1))
    } else if (e.key === 'Enter' && open && active >= 0 && options[active]) {
      e.preventDefault()
      pick(options[active])
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div
      className={styles.combo}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false) }}
    >
      <input
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        autoComplete="off"
        className={clsx(formStyles.input, invalid && formStyles.inputError)}
        style={mono ? { fontFamily: 'var(--aptean-font-mono)', fontSize: 13 } : undefined}
        value={inputValue}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onChange={(e) => { setOpen(true); setActive(-1); onInputChange(e.target.value) }}
        onKeyDown={onKeyDown}
      />
      {open && !disabled && (
        <ul id={listId} role="listbox" aria-label={`${ariaLabel} results`} className={styles.comboList}>
          {loading && options.length === 0 && <li className={styles.comboEmpty}>Loading from Azure…</li>}
          {!loading && options.length === 0 && <li className={styles.comboEmpty}>{emptyText}</li>}
          {options.map((o, i) => (
            <li
              key={o.id}
              role="option"
              aria-selected={!!o.selected}
              aria-disabled={o.disabled || undefined}
              className={clsx(styles.comboOption, i === active && styles.comboOptionActive, o.selected && styles.comboOptionSelected, o.disabled && styles.comboOptionDisabled)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(o)}
            >
              <span>{o.label}</span>
              {o.description && <span className={styles.comboOptionSub}>{o.description}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

interface SearchableSelectProps extends Omit<ShellProps, 'children'> {
  options: ComboOption[]
  onSelect: (id: string) => void
  /** Called 300 ms after the user stops typing (and with "" when an option is picked). */
  onSearch: (q: string) => void
  /** What is selected right now (shown under the search box). */
  summary?: ReactNode
  placeholder?: string
  emptyText?: string
  disabled?: boolean
}

/** Search box + results list over a live catalog; the query is debounced before it reaches the API. */
export function SearchableSelect({ options, onSelect, onSearch, summary, placeholder, emptyText, disabled, ...shell }: SearchableSelectProps) {
  const [query, setQuery] = useState('')
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => clearTimeout(timer.current), [])

  const onInputChange = (v: string) => {
    setQuery(v)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => onSearch(v.trim()), SEARCH_DEBOUNCE_MS)
  }
  const onPick = (id: string) => {
    clearTimeout(timer.current)
    setQuery('')
    onSearch('')
    onSelect(id)
  }

  return (
    <FieldFrame {...shell}>
      <Combobox
        ariaLabel={`Search ${shell.label.toLowerCase()}`}
        inputValue={query}
        onInputChange={onInputChange}
        onPick={onPick}
        options={options}
        placeholder={placeholder ?? `Search ${shell.label.toLowerCase()}…`}
        loading={shell.catalog?.isLoading || shell.catalog?.isFetching}
        emptyText={emptyText}
        invalid={!!shell.error}
        disabled={disabled}
      />
      {summary && <div className={styles.summaryBox} style={{ marginTop: 6 }}>{summary}</div>}
    </FieldFrame>
  )
}

interface CheckFieldProps {
  label: ReactNode
  description?: ReactNode
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
  error?: string
  full?: boolean
}

export function CheckField({ label, description, checked, onChange, disabled, error, full = true }: CheckFieldProps) {
  return (
    <div style={full ? { gridColumn: '1/-1' } : undefined}>
      <label className={clsx(styles.checkRow, disabled && styles.checkRowDisabled)}>
        <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} style={{ marginTop: 3 }} />
        <span>
          {label}
          {description && <span className={formStyles.fieldHint} style={{ display: 'block' }}>{description}</span>}
        </span>
      </label>
      {error && <span className={formStyles.errorMsg}>{error}</span>}
    </div>
  )
}

/** An Azure option the portal does not support yet — visible, disabled, with the reason. Never a working switch. */
export function DisabledCheck({ label, reason = 'Not supported by the portal yet' }: { label: string; reason?: string }) {
  return <CheckField label={label} description={reason} checked={false} onChange={() => undefined} disabled />
}
